/**
 * Minimal Cognito Hosted UI OAuth2 authorization-code flow.
 *
 * Design note: tokens are held in memory only (see AuthContext). The
 * code/state pair survives the redirect via sessionStorage so the
 * one-shot exchange is resistant to accidental refreshes on the
 * callback page, but the resulting tokens are never persisted.
 */

import {
  HOSTED_UI_DOMAIN,
  USER_POOL_CLIENT_ID,
  isAuthConfigured,
} from "../config";

const STATE_STORAGE_KEY = "agent-gov-demo.oauth-state";
const REDIRECT_AFTER_LOGIN_KEY = "agent-gov-demo.post-login-path";

/** Response from Cognito's `/oauth2/token` endpoint for the code grant. */
export interface CognitoTokenResponse {
  id_token: string;
  access_token: string;
  refresh_token?: string;
  expires_in: number;
  token_type: string;
}

/** Claims we care about inside the Cognito ID token. */
export interface CognitoIdTokenClaims {
  sub: string;
  email?: string;
  "cognito:username"?: string;
  "cognito:groups"?: string[];
  exp: number;
  iat: number;
  [key: string]: unknown;
}

/**
 * Decode a JWT payload. Returns null when the token is malformed.
 *
 * This is decode-only; the JWT is validated server-side by the API Gateway
 * Cognito authorizer. The SPA trusts the ID token for UX (routing) only.
 */
export function decodeJwtPayload(token: string): CognitoIdTokenClaims | null {
  const parts = token.split(".");
  if (parts.length !== 3) return null;
  try {
    const payload = parts[1]
      .replace(/-/g, "+")
      .replace(/_/g, "/");
    const padded = payload.padEnd(
      payload.length + ((4 - (payload.length % 4)) % 4),
      "=",
    );
    const decoded = atob(padded);
    return JSON.parse(decoded) as CognitoIdTokenClaims;
  } catch {
    return null;
  }
}

/** Current window origin used as the OAuth2 redirect URI. */
export function getRedirectUri(): string {
  return `${window.location.origin}/auth/callback`;
}

/** Generate a random URL-safe string for CSRF protection. */
function generateRandomState(): string {
  const bytes = new Uint8Array(16);
  crypto.getRandomValues(bytes);
  return Array.from(bytes, (b) => b.toString(16).padStart(2, "0")).join("");
}

/**
 * Redirect the browser to the Cognito Hosted UI login page.
 *
 * `postLoginPath` is the in-SPA path the user should land on after the
 * callback succeeds. We stash it in sessionStorage so the callback page
 * can navigate there once tokens are exchanged.
 */
export function beginLogin(postLoginPath: string = "/"): void {
  if (!isAuthConfigured()) {
    throw new Error(
      "Cognito not configured. Set VITE_HOSTED_UI_DOMAIN and VITE_USER_POOL_CLIENT_ID.",
    );
  }
  const state = generateRandomState();
  sessionStorage.setItem(STATE_STORAGE_KEY, state);
  sessionStorage.setItem(REDIRECT_AFTER_LOGIN_KEY, postLoginPath);
  const params = new URLSearchParams({
    client_id: USER_POOL_CLIENT_ID,
    response_type: "code",
    scope: "openid email profile",
    redirect_uri: getRedirectUri(),
    state,
  });
  window.location.assign(`${HOSTED_UI_DOMAIN}/oauth2/authorize?${params}`);
}

/** Redirect the browser to the Cognito Hosted UI logout endpoint. */
export function beginLogout(): void {
  if (!isAuthConfigured()) {
    window.location.assign("/");
    return;
  }
  const params = new URLSearchParams({
    client_id: USER_POOL_CLIENT_ID,
    logout_uri: window.location.origin,
  });
  window.location.assign(`${HOSTED_UI_DOMAIN}/logout?${params}`);
}

/** Read and clear the saved post-login path. */
export function consumePostLoginPath(): string {
  const path = sessionStorage.getItem(REDIRECT_AFTER_LOGIN_KEY);
  sessionStorage.removeItem(REDIRECT_AFTER_LOGIN_KEY);
  return path && path.startsWith("/") ? path : "/";
}

/** Validate the state parameter returned on the OAuth2 callback. */
export function verifyState(receivedState: string): boolean {
  const stored = sessionStorage.getItem(STATE_STORAGE_KEY);
  sessionStorage.removeItem(STATE_STORAGE_KEY);
  return stored !== null && stored === receivedState;
}

/**
 * Exchange the authorization code for tokens at Cognito's `/oauth2/token`
 * endpoint. The SPA client has no client secret (public client), so the
 * request body carries only the code and client id.
 */
export async function exchangeCodeForTokens(
  code: string,
): Promise<CognitoTokenResponse> {
  if (!isAuthConfigured()) {
    throw new Error("Cognito not configured; cannot exchange code.");
  }
  const body = new URLSearchParams({
    grant_type: "authorization_code",
    client_id: USER_POOL_CLIENT_ID,
    code,
    redirect_uri: getRedirectUri(),
  });
  const response = await fetch(`${HOSTED_UI_DOMAIN}/oauth2/token`, {
    method: "POST",
    headers: { "Content-Type": "application/x-www-form-urlencoded" },
    body: body.toString(),
  });
  if (!response.ok) {
    const text = await response.text();
    throw new Error(
      `Cognito token exchange failed (${response.status}): ${text}`,
    );
  }
  return (await response.json()) as CognitoTokenResponse;
}
